import { useEffect, useRef, useCallback } from 'react';
import { useFirebase } from 'containers/FirebaseContext';
import { useCurrentUser } from 'store/users/selectors';
import { createAdminActivityLogger, ACTIVITY_TYPES, ACTIVITY_MODULES } from 'utils/adminActivityLogger';
import { useLocation } from 'react-router-dom';

export const useAdminActivityLogger = () => {
    const { firestore , firebaseUser } = useFirebase();
    const currentUser = useCurrentUser();
    const location = useLocation();
    const loggerRef = useRef(null);
    const lastLocationRef = useRef(null);
    const cleanupIntervalRef = useRef(null);

    useEffect(() => {
        if (firestore && currentUser && currentUser.role === 'owe_admin') {
            loggerRef.current = createAdminActivityLogger(firestore, currentUser, firebaseUser);

            cleanupIntervalRef.current = setInterval(() => {
                if (loggerRef.current) {
                    loggerRef.current.cleanupOldActivities();
                }
            }, 24 * 60 * 60 * 1000);
        }

        return () => {
            if (cleanupIntervalRef.current) {
                clearInterval(cleanupIntervalRef.current);
            }
        };
    }, [firestore, currentUser, firebaseUser]);

    useEffect(() => {
        if (loggerRef.current && location.pathname !== lastLocationRef.current) {
            lastLocationRef.current = location.pathname;
            
            loggerRef.current.logActivity({
                activityType: ACTIVITY_TYPES.VIEW,
                module: getModuleFromPath(location.pathname),
                action: 'navigate',
                resourceId: location.pathname,
                resourceName: location.pathname,
                details: {
                    pathname: location.pathname,
                    search: location.search
                }
            });
        }
    }, [location]);

    const logBookActivity = useCallback((activityType, bookId, bookName, details = {}) => {
        if (loggerRef.current) {
            return loggerRef.current.logBookActivity(activityType, bookId, bookName, details);
        }
    }, []);

    const logBookToolActivity = useCallback((activityType, toolName, bookId, pageId, details = {}) => {
        if (loggerRef.current) {
            return loggerRef.current.logBookToolActivity(activityType, toolName, bookId, pageId, details);
        }
    }, []);

    const logUserActivity = useCallback((activityType, targetUserId, targetUserName, details = {}) => {
        if (loggerRef.current) {
            return loggerRef.current.logUserActivity(activityType, targetUserId, targetUserName, details);
        }
    }, []);

    const logPermissionActivity = useCallback((targetUserId, targetUserName, permissionChanges) => {
        if (loggerRef.current) {
            return loggerRef.current.logPermissionActivity(targetUserId, targetUserName, permissionChanges);
        }
    }, []);

    const logSettingsActivity = useCallback((activityType, settingKey, settingValue) => {
        if (loggerRef.current) {
            return loggerRef.current.logSettingsActivity(activityType, settingKey, settingValue);
        }
    }, []);

    const logPlacementExamActivity = useCallback((activityType, examId, examName, details = {}) => {
        if (loggerRef.current) {
            return loggerRef.current.logPlacementExamActivity(activityType, examId, examName, details);
        }
    }, []);

    const logCustomActivity = useCallback((options) => {
        if (loggerRef.current) {
            return loggerRef.current.logActivity(options);
        }
    }, []);

    const getRecentActivities = useCallback(async (days = 7, limitCount = 100) => {
        if (loggerRef.current) {
            return await loggerRef.current.getRecentActivities(days, limitCount);
        }
        return [];
    }, []);

    const getActivitiesByModule = useCallback(async (module, days = 7) => {
        if (loggerRef.current) {
            return await loggerRef.current.getActivitiesByModule(module, days);
        }
        return [];
    }, []);

    const getLastActivity = useCallback(async () => {
        if (loggerRef.current) {
            return await loggerRef.current.getLastActivity();
        }
        return null;
    }, []);

    const getActivityStats = useCallback(async (days = 7) => {
        if (loggerRef.current) {
            return await loggerRef.current.getActivityStats(days);
        }
        return null;
    }, []);

    const cleanupOldActivities = useCallback(async () => {
        if (loggerRef.current) {
            return await loggerRef.current.cleanupOldActivities();
        }
        return 0;
    }, []);

    return {
        logBookActivity,
        logBookToolActivity,
        logUserActivity,
        logPermissionActivity,
        logSettingsActivity,
        logPlacementExamActivity,
        logCustomActivity,
        getRecentActivities,
        getActivitiesByModule,
        getLastActivity,
        getActivityStats,
        cleanupOldActivities,
        isReady: !!loggerRef.current
    };
};

const getModuleFromPath = (pathname) => {
    if (pathname.includes('/ebook')) return ACTIVITY_MODULES.BOOKS;
    if (pathname.includes('/book')) return ACTIVITY_MODULES.BOOKS;
    if (pathname.includes('/users')) return ACTIVITY_MODULES.USERS;
    if (pathname.includes('/settings')) return ACTIVITY_MODULES.SETTINGS;
    if (pathname.includes('/exam')) return ACTIVITY_MODULES.PLACEMENT_EXAM;
    if (pathname.includes('/grammar')) return ACTIVITY_MODULES.GRAMMAR_DRILL;
    if (pathname.includes('/quotes')) return ACTIVITY_MODULES.QUOTES;
    if (pathname.includes('/standard')) return ACTIVITY_MODULES.STANDARDS;
    if (pathname.includes('/class')) return ACTIVITY_MODULES.CLASSES;
    if (pathname.includes('/assignment')) return ACTIVITY_MODULES.ASSIGNMENTS;
    if (pathname.includes('/activity')) return ACTIVITY_MODULES.ACTIVITIES;
    if (pathname.includes('/quiz')) return ACTIVITY_MODULES.QUIZZES;
    if (pathname.includes('/story')) return ACTIVITY_MODULES.STORY;
    if (pathname.includes('/vocab')) return ACTIVITY_MODULES.VOCAB;
    if (pathname.includes('/slide')) return ACTIVITY_MODULES.SLIDES;
    if (pathname.includes('/rubric')) return ACTIVITY_MODULES.RUBRICS;
    if (pathname.includes('/lesson')) return ACTIVITY_MODULES.LESSON_PLANS;
    if (pathname.includes('/worksheet')) return ACTIVITY_MODULES.WORKSHEETS;
    return 'general';
};

/**
 * Determines the action name based on updated fields and message
 * @param {Array<string>} updatedFields - Array of field names that were updated
 * @param {string} message - Message describing the update
 * @returns {string} - Action name (e.g., 'answer_page_removed', 'pdf_added', 'hyperlinks_update')
 */
export const getActionFromFieldsAndMessage = (updatedFields, message) => {
    const fieldActionMap = {
        'hyperlinks': 'hyperlinks',
        'pagesTeacher': 'answer_page',
        'pacingTeacher': 'pacing_guide',
        'syllabusTeacher': 'syllabus',
        'pagesPDF': 'pdf',
        'teacherEdition': 'teacher_edition',
        'subTopics': 'units',
        'lessonPlan': 'lesson_plan',
        'superLesson': 'super_lesson',
        'differentiationLesson': 'differentiation',
        'sentence_starter_data': 'sentence_starter',
        'assignmentPlan': 'quiz',
        'unit_exam': 'unit_exam',
        'workbook_plan': 'workbook',
        'assignment_book_link_plan': 'assessment_book_link',
        'main_book': 'assessment_book',
        'rubrics': 'rubrics',
        'ai_content': 'game_ai_content',
        'pages': 'page',
        'description': 'book_description',
        'img': 'cover_image',
        'printable': 'book_printable',
        'isNotAssignable': 'book_assignable',
        'markAsMainBook': 'book_main_book',
        'onlyTeacher': 'book_teacher_only',
        'is_assessment_book': 'book_assessment',
        'book_type': 'book_type',
        'level': 'book_level',
        'language': 'book_language',
        'type': 'book_type_category'
    };

    let baseAction = null;
    for (const field of updatedFields) {
        if (fieldActionMap[field]) {
            baseAction = fieldActionMap[field];
            break;
        }
    }

    if (!baseAction) {
        return 'book_data_update';
    }

    const messageLower = (message || '').toLowerCase();
    
    if (messageLower.includes('removed') || messageLower.includes('removed!')) {
        return `${baseAction}_removed`;
    } else if (messageLower.includes('added') || messageLower.includes('added!') || messageLower.includes('uploaded successfully')) {
        return `${baseAction}_added`;
    } else if (messageLower.includes('updated') || messageLower.includes('updated!') || messageLower.includes('is updated')) {
        return `${baseAction}_update`;
    } else if (messageLower.includes('uploaded')) {
        return `${baseAction}_upload`;
    } else {
        return `${baseAction}_update`;
    }
};

export default useAdminActivityLogger;

